/**
 * Feature Flag Configuration
 *
 * Complete mapping of frontend feature names to backend API paths.
 *
 * Backend API Structure:
 * - features_status_config
 *   - geolocation_providers
 *   - kyc_providers
 *   - sportsbook_providers
 *   - admin_panel_features
 *   - payment_providers
 *   - social_features
 *   - sportsbook_features
 *   - 2fa
 *   - additional_features
 *   - casino_providers
 *   - bonus
 */

/**
 * Complete Feature Mappings
 * Maps simple frontend names to nested backend paths in features_status_config
 *
 * Structure: 'frontendName': 'category.feature_name'
 *
 * Categories:
 * - geolocation_providers (1 feature)
 * - kyc_providers (2 features)
 * - sportsbook_providers (4 features)
 * - admin_panel_features (16 features)
 * - payment_providers (7 features)
 * - social_features (10 features)
 * - sportsbook_features (8 features)
 * - 2fa (2 features)
 * - additional_features (13 features)
 * - casino_providers (6 features)
 * - bonus (1 feature)
 *
 * Total: 70 features mapped
 */
export const FEATURE_MAPPINGS = {
  // GEOLOCATION PROVIDERS
    'seon': 'geolocation_providers.seon',

  // KYC PROVIDERS
    'plaid': 'kyc_providers.plaid',
    'surt': 'kyc_providers.surt',
  // Second-level gate for Compliance → KYC: the page and sidebar entry gate on
  // `surt`, and the checkpoints panel additionally requires this flag.
    'surtCheckpoints': 'kyc_providers.checkpoints.enabled',

  // SPORTSBOOK PROVIDERS
    'onefeed': 'sportsbook_providers.onefeed',
    'sportsradar': 'sportsbook_providers.sportsradar',
    'dst': 'sportsbook_providers.dst',
    'oddin': 'sportsbook_providers.oddin',
    'sportradar': 'sportsbook_providers.sportradar',

  // ADMIN PANEL FEATURES
    'promotionsManagement': 'admin_panel_features.promotions_management',
    'reports': 'admin_panel_features.reports',
    'siteConfiguration': 'admin_panel_features.site_configuration',
    'casinoManagement': 'admin_panel_features.casino_management',
    'lowFbo': 'admin_panel_features.low_fbo',
    'adminPeerToPeer': 'admin_panel_features.admin_peer_to_peer',
    'marketing': 'admin_panel_features.marketing',
    'manualWithdrawalReview': 'admin_panel_features.manual_withdrawal_review',
    'sportsbookManagement': 'admin_panel_features.sportsbook_management',
    'fraudDetectionSystems': 'admin_panel_features.fraud_detection_systems',
    'notificationsEmailManagement': 'admin_panel_features.notifications_email_management',
    'staffPermissions': 'admin_panel_features.staff_permissions',
    'responsibleGaming': 'admin_panel_features.responsible_gaming',
    'paymentsTransactions': 'admin_panel_features.payments_transactions',
    'transactions': 'admin_panel_features.payments_transactions',
    'dashboard': 'admin_panel_features.dashboard',
  // TODO(PAMENG-1820): rollout fallback for SPORTSBOOK; drop after rollout.
    'sportsbook_v2': 'sportsbook_features.sportsbook_v2_available',
    'sgp_limits': 'admin_panel_features.sgp_limits',
    'transactions_wallet': 'admin_panel_features.transactions_wallet',
    'transactions_overview': 'admin_panel_features.transactions_overview',
    'transactions_reports': 'admin_panel_features.transactions_reports',
    'manual_withdrawal_review': 'admin_panel_features.manual_withdrawal_review',
  // VER-444: Compliance → Duplicate Identity Review queue (default off).
    'duplicateIdentityReview': 'compliance.duplicate_identity_review',
    'sportsbook_kpi': 'admin_panel_features.sportsbook_kpi',
    'transactions_routing': 'admin_panel_features.transactions_routing',
    'casino_fm_v2': 'admin_panel_features.casino_fm_v2',
    'config_mode_enabled': 'admin_panel_features.config_mode_enabled',

  // PAYMENT PROVIDERS
    'evervault': 'payment_providers.evervault',
    'palmpay': 'payment_providers.palmpay',
    'finix': 'payment_providers.finix',
    'paystack': 'payment_providers.paystack',
    'nmi': 'payment_providers.nmi',
    'opay': 'payment_providers.opay',
    'maverick': 'payment_providers.maverick',

  // SOCIAL FEATURES
    'comments': 'social_features.comments',
    'duplicateBets': 'social_features.duplicate_bets',
    'liveChat': 'social_features.live_chat',
    // TODO(PAMENG-1820): rollout fallback for CASINO (casino product); drop after rollout.
    'casino': 'social_features.casino',
    'discoverFeed': 'social_features.discover_feed',
    'feeds': 'social_features.feeds',
    'dms': 'social_features.dms',
    'friends': 'social_features.friends',
    'likes': 'social_features.likes',
    'shareCasinoBet': 'social_features.share_casino_bet',

  // SPORTSBOOK FEATURES
    'codds': 'sportsbook_features.codds',
    'oneFeed': 'sportsbook_features.one_feed',
    'statHub': 'sportsbook_features.stat_hub',
    'banner': 'sportsbook_features.banner',
    'playerHeadshots': 'sportsbook_features.player_headshots',
    'populerBet': 'sportsbook_features.populer_bet',
    'teamLeagueLogos': 'sportsbook_features.team_league_logos',
    'vaix': 'sportsbook_features.vaix',

  // 2FA PROVIDERS
    'aws2fa': '2fa.aws',
    'infobip2fa': '2fa.infobip',
    'seonTriggered2fa': '2fa.seon_triggered',

  // ADDITIONAL FEATURES
    'supportChat': 'additional_features.support_chat',
    'braze': 'additional_features.braze',
    'googleSignin': 'additional_features.google_signin',
  // TODO(PAMENG-1820): rollout fallback for POKER; drop after rollout.
    'poker': 'additional_features.poker',
    'easyToReadline': 'additional_features.easy_to_readline',
    'logrocket': 'additional_features.logrocket',
    'p2p': 'additional_features.p2p',
    'facebookSignin': 'additional_features.facebook_signin',
    'creditDebitPayments': 'additional_features.credit_debit_payments',
    'firebaseAnalytics': 'additional_features.firebase_analytics',
  // gamePass removed — migrated to TRIVELTA_PRODUCTS.GAME_PASS (PAMENG-1820)
    'gamePassPam': 'additional_features.game_pass_pam',
    'appsflyer': 'additional_features.appsflyer',
    'appleSignin': 'additional_features.apple_signin',
  // TODO(PAMENG-1820): rollout fallback for PIKKEM (likely laggard — not in
  // the live v2 response when the migration shipped); drop after rollout.
    'pikkem': 'additional_features.pikkem',
    'liveCasino': 'additional_features.live_casino',

  // CASINO PROVIDERS
    'aggregator': 'casino_providers.aggregator',
    'threeoaks': 'casino_providers.threeoaks',
    'spribe': 'casino_providers.spribe',
    'evolution': 'casino_providers.evolution',
    'booming': 'casino_providers.booming',
    'betcore': 'casino_providers.betcore',
    'royal': 'casino_providers.royal',
    'saGaming': 'casino_providers.sa_gaming',
    '3oaks': 'casino_providers.3oaks',
    'jacktop': 'casino_providers.jacktop',
    'pragmatic': 'casino_providers.pragmatic',
    'goldenrace': 'casino_providers.goldenrace',
    'bgaming': 'casino_providers.bgaming',
    'iconic21': 'casino_providers.iconic21',
    'kerma': 'casino_providers.kerma',

  // CASINO FEATURES
  // CASENG-995: per-client gate for Casino → Configuration → Pages. Off by
  // default — flip `casino_features.casino_configuration_pages` in that
  // client's platform-configs to show the tab and unlock the route.
    'casinoConfigurationPages': 'casino_features.casino_configuration_pages',
  // CASENG-1119: per-client gate for the Features → Jackpot card. Off by
  // default — the payload is an object (`{ enabled: true }`), so the path
  // must include `.enabled` (the parent is not a boolean).
    'casinoJackpot': 'casino_features.jackpot.enabled',
  // CASENG-1054: renders TCM-translated collection/tag/page names in the
  // admin's language on casino curation screens; off by default — flag-off
  // shows the canonical name everywhere.
    'casinoPamCurationTranslations': 'casino_features.casino_pam_curation_translations',

  // GAME PASS
  // FE-2261: gates the production type-to-confirm End Season modal; off by
  // default — flag-off falls back to the legacy single-click confirmation.
    'endSeasonConfirmV2': 'game_pass.end_season_confirm_v2',

  // POKER
  // POKENG-191: gates the Cash Game Limits config page (v2 per-config API,
  // POKENG-142); off by default — flag-off shows the "Coming soon"
  // placeholder and skips the v2 config fetch entirely.
    'pokerCashGameConfigV2': 'poker.cash_game_config_v2',

  // DYNAMIC BONUSES
    'dynamicBonuses': 'bonus.dynamic_bonuses',
    'dynamicBonusesInternal': 'bonus.dynamic_bonuses_internal',
    'dynamicBonusesExternal': 'bonus.dynamic_bonuses_external',
  // BONENG-1181: Duplicate opens Campaign Builder with a clone preview
  // instead of POSTing a draft immediately. Off by default — flag-off keeps
  // persist-on-click Duplicate for campaigns and hides template/detail Duplicate.
    'dynamicBonusesDuplicateBuilder': 'bonus.campaign_duplicate_builder',
  // BONENG-1067: unsaved campaign test-send from the create wizard.
    'draftCampaignTestSend': 'bonus.draft_campaign_test_send',
  // BONENG-1093: test sends cover every step, not just the first. Same key as
  // the backend gate, so the modal copy never promises more than is delivered.
    'multiStepCampaignTestSend': 'bonus.multi_step_campaign_test_send',
    'referralCampaign': 'bonus.referral_campaign',
    'freeSpinsIssuance': 'bonus.free_spins_issuance',
  // BONENG-913: gates the "% of trigger amount" reward-sizing input in the
  // campaign wizard; off by default — flag-off keeps fixed $ amounts only.
    'percentOfTriggerRewards': 'bonus.percent_of_trigger_rewards',
  // BONENG-1274: steps may fire on different events and award more than once
  // in one run. Off by default — flag-off keeps the legacy same-event rule.
    'multiAwardSteps': 'bonus.multi_award_steps',
  // BONENG-966: gates the Reached/Used distinct-user columns on the Campaigns
  // list; off by default. Separate from the backend's bonus.campaign_metrics so
  // counters can accumulate before the columns are shown.
    'campaignMetrics': 'bonus.campaign_metrics_ui',
  // BONENG-1160: server-side pagination on the Campaigns tab; off by default
  // keeps the unpaginated client-side merge.
    'campaignListPagination': 'bonus.campaign_list_pagination',
  // BONENG-788: gates Dynamic Bonus Builder UX (NL result labels, Who
  // selector, Bonus Type picker); off by default — flag-off keeps raw
  // type_key (offer|award) Result labels and the existing wizard chrome.
    'dynamicBonusBuilder': 'bonus.dynamic_bonus_builder',
  // BONENG-1064: gates the destination deeplink suggestion on campaign
  // step banners; off by default — flag-off keeps Destination without
  // the restriction-based Add prompt.
    'dynamicBonusDeeplinkSuggestion': 'bonus.dynamic_bonus_deeplink_suggestion',
  // BONENG-1137: gates persisting provider_id on casino_game banner deeplinks.
  // Off by default — flag-off saves game_id only so older bonus-platform
  // validators still accept the payload during staggered deploys.
    'casinoGameDeeplinkProvider': 'bonus.casino_game_deeplink_provider',
  // BONENG-813: gates PAM standalone/informational marketing banner
  // configuration (Display Banner campaign type); off by default.
    'standaloneDisplayBanner': 'bonus.standalone_display_banner',
  // BONENG-1189: Display Banner as a step Result (picker + Banner Creative)
  // instead of the Campaign Type tab. Off by default — flag-off keeps the
  // BONENG-813 tab and hides Display Banner from the Result dropdown.
    'displayBannerV2': 'bonus.display_banner_v2',
  // BONENG-432: gates Cashback Offers in the PAM bonus catalog / campaign
  // wizard. Off by default — flag-off hides award_cashback from the Result
  // picker. Engine evaluation uses the same key (bonus.cashback_offers).
    'cashbackOffers': 'bonus.cashback_offers',
  // BONENG-1236: Refund on Loss becomes Credit Wallet [primary cash] with
  // no wallet Restriction. Off by default — flag-off keeps the current
  // wallet chip, "Refund on Loss" label, and Free Pick coupling.
    'creditWalletCashResult': 'bonus.credit_wallet_cash_result',
  // BONENG-441: gates Future Bonus in the Dynamic Bonuses result picker and
  // league/trait lookups; off by default.
    'futureBonusesPam': 'bonus.future_bonuses_pam',
  // BONENG-1130: beta lockdown for Dynamic Bonuses. Flag on hides the
  // Campaigns-page Segments toolbar button. Missing/off locks the segment
  // audience card (visible but not selectable) and hides the User Referral
  // / Affiliate Referral campaign-type pills. Backend independently 422s
  // user_in_segment. Seed true at first deploy, then flip to false after
  // PAM + backend land.
    'dynamicBonusesBetaMode': 'bonus.dynamic_bonuses_beta_mode',

    'featureQuickAccess': 'admin_panel_features.feature_quick_access',

  /*  LEGACY
        These are alternative names used in existing code that point to
        the same features already defined above. They are kept for backward
        compatibility so existing components don't break.
        All of these features exist in the API and work correctly. */
    'promotions': 'admin_panel_features.promotions_management',        // Alias for promotionsManagement
    'featureManagement': 'admin_panel_features.site_configuration',    // Alias for siteConfiguration
    'finixDelayedPayment': 'payment_providers.finix',                  // Alias for finix
};

/**
 * API Configuration
 */
export const API_CONFIG = {
    PLATFORM_CONFIG_ENDPOINT: '/admin-panel/platform/v1/config',
    PLATFORM_CONFIG_V2_ENDPOINT: '/admin-panel/platform/v2/config',
  USE_API: true, // Using API for GET (read)
};
